<template>
  <div class="wizard">
    <ul class="wizard__steps">
      <li class="wizard__step"
        :class="{
          'active': currentStep === index,
          'vgw-mobile': isMobile,
        }"
        :style="wizardStepStyle"
        v-for="(step, index) of steps" :key="index">
        <span class="wizard__step__line" :class="{'vgw-mobile': isMobile}"/>
        <span class="wizard__step__label">{{step.label}}</span>
      </li>
    </ul>
    <span
      class="wizard__arrow"
      :style="arrowStyle">
    </span>
    <div ref="wizard-body" class="wizard__body" :class="{'vgw-mobile': isMobile}">
      <div :key="currentSlot" class="wizard__body__step">
        <slot :name="currentSlot"></slot>
      </div>
      <div class="wizard__body__actions clearfix" v-if="!options[currentStep].hideActions">
        <a
          v-if="backEnabled"
          class="wizard__back pull-left"
          @click="goBack()">
          <span>{{previousStepLabel}}</span>
        </a>
        <a
          :class="{'disabled': options[currentStep].nextDisabled}"
          v-if="currentStep !== steps.length - 1" class="wizard__next pull-right"
          @click="goNext()">
          <span>{{nextStepLabel}}</span>
        </a>
        <a
          :class="{'disabled': options[currentStep].nextDisabled}"
          v-if="currentStep == steps.length - 1" class="wizard__next pull-right final-step" @click="$emit('submit')">
          {{finalStepLabel}}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'wizard',
  props: {
    steps: {},
    previousStepLabel: {default: 'Back'},
    nextStepLabel: {default: 'Next'},
    finalStepLabel: {default: 'Save'},
    onNext: {},
    onBack: {},
  },
  watch: {
    steps: {
      handler() {
        this.parseOptions();
      },
      immediate: true,
    }
  },
  data () {
    return {
      currentStep: 0,
      isMounted: false,
      resizer: null,
      isMobile: false,
      options: [],
    };
  },
  computed: {
    wizardStepStyle() {
      if (this.isMobile) {
        return {
          width: '100%',
        };
      } 
      return {
        width: `${100 / this.steps.length}%`,
      };
    },
    arrowStyle() {
      let style = {};
      let stepSize = 100 / this.steps.length;
      let currentStepStart = stepSize * this.currentStep;
      style.left = `${currentStepStart}%`;
      style.width = `${stepSize}%`;

      return style;
    },
    currentSlot() {
      return this.steps[this.currentStep].slot;
    },
    backEnabled() {
      return this.currentStep !== 0;
    }
  },
  methods: {
    goNext (skipFunction) {
      if (!skipFunction && typeof this.onNext === 'function') {
        if (!this.onNext(this.currentStep)) {
          // returned false. don't do anything
          return;
        }
      }
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++;
      }
    },
    goBack (skipFunction) {
      if (!skipFunction && typeof this.onBack === 'function') {
        if (!this.onBack(this.currentStep)) {
          // returned false. don't do anything
          return;
        }
      }
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    goTo(step) {
      if (Number.isInteger(step) &&
          step < this.steps.length &&
          step >= 0) {
        this.currentStep = step;
      }
    },

    parseOptions() {
      this.options = [];
      for (let i = 0; i < this.steps.length; i++) {
        this.options.push(this.steps[i].options ? this.steps[i].options : {});
      }
    },
    handleResize() {
      console.log('handle resize')
      if (this.resizer) {
        clearTimeout(this.resizer);
      }
      this.resizer = setTimeout(() => {
        console.log('resizing...');
        this.isMobile = this.$refs['wizard-body'].clientWidth < 620;
      }, 100);
    },
  },
  mounted() {
    this.isMobile = this.$refs['wizard-body'].clientWidth < 620;
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  }
};
</script>

<style scoped lang="scss">
@import "../assets/scss/_colors.scss";

/* Utilities
*******************************/
.pull-left{
  float:  left !important;
}
.pull-right{
  float:  right !important;
}
.clearfix::after {
  display: block;
  content: "";
  clear: both;
}
/* Header Steps
*******************************/
.stretch {
  width: 100%;
  display: inline-block;
  font-size: 0;
  line-height: 0;
}

.wizard {
  position: relative;
  margin-top: 90px;
  margin-left: 100px;
  margin-right: 100px;
  width: auto;

  .wizard__steps {
    text-align: justify;
    -ms-text-justify: distribute-all-lines;
    text-justify: distribute-all-lines;
    padding:  0;
    height:  70px;
    position:  relative;
  }

  .wizard__step {
    &.active .wizard__step__label {
      color: $black-1;
      font-weight: bold;
    }
    height: 70px;
    vertical-align: bottom;
    display: inline-block;
    text-align: center;
    position: relative;

    .wizard__step__label {
      color: $grey-2;
      font-size: 24px;
      font-weight: normal;
    }

    .wizard__step__line{
      position: absolute;
      width: 100%;
      left: 0;
      bottom: 12px;
      height: 6px;
      background-color: #e3e3e3;
    }
  }

  /* Wizard body
  *******************************/
  .wizard__body {
    min-height:  400px;
    position: relative;
    padding-bottom: 50px;

    .wizard__body__step {
      padding:  16px;
    }

    .wizard__body__actions {
      display: flex;
      justify-content: flex-end;
      background-color: transparent;

      position:  absolute;
      bottom:  0px;
      height:  50px;
      width:  100%;

      .wizard__back {
        &:hover,
        &:focus {
          color: $blue-1;
          background-color: transparent;
        }
        color: $blue-1;
        background-color: transparent;
      }

      .wizard__next {
        &:hover,
        &:focus {
          background-color: #0D9EC4;
        }

        width: 200px;
        background-color: $blue-1;
      }

      a {
        &.disabled {
          cursor: not-allowed;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          opacity: 0.5;
        }

        width:  120px;
        height:  100%;
        display: block;
        background-color: #51abe4;
        color:  white;
        font-size: 24px;
        font-weight: 400;
        border-radius: 8px;
        text-align: center;
        line-height: 50px;
        cursor: pointer;
        -webkit-transition: background-color 0.3s;
        -o-transition: background-color 0.3s;
        transition: background-color 0.3s;

        span { vertical-align: top; }
      }
    }
  }

  .wizard__arrow {
    position:  absolute;
    display: block;
    width:  30px;
    height:  6px;
    top:  52px;
    background-color: $blue-1;
    z-index: 2;
    -webkit-transition: left 0.3s;
    -o-transition: left 0.3s;
    transition: left 0.3s;
  }
}

/* mobile */
.wizard__body.vgw-mobile{
  margin-left: 10px;
  margin-right: 10px;
}
.wizard__step.vgw-mobile{
  display: none;
}
.wizard__step .wizard__step__line.vgw-mobile{
  display: none;
}
.wizard__step.active.vgw-mobile{
  display: inline-block;
}
</style>
